import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { cmsApi } from '@/lib/api';

const iconForSocial = (iconKey) => {
  switch (iconKey) {
    case 'x':
      return 'X';
    case 'linkedin':
      return 'in';
    case 'substack':
      return 'S';
    case 'youtube':
      return 'YT';
    default:
      return '';
  }
};

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [footer, setFooter] = useState(null);

  useEffect(() => {
    const fetchFooter = async () => {
      try {
        const res = await cmsApi.getFooter();
        setFooter(res);
      } catch (error) {
        console.error('Failed to load footer settings:', error);
      }
    };
    fetchFooter();
  }, []);

  return (
    <footer className="bg-primary text-primary-foreground">
      {/* Main Footer Content */}
      <div className="container-wide py-16 sm:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          {/* Brand Column */}
          <div className="md:col-span-5">
            {/* Logo */}
            <Link to="/" className="inline-flex items-center gap-3 mb-6">
              <div className="relative flex items-center justify-center w-10 h-10 bg-primary-foreground rounded-sm">
                <span className="font-serif text-lg font-bold text-primary tracking-tighter">
                  TAR
                </span>
              </div>
              <span className="font-serif text-2xl font-bold tracking-tight">
                TheAnalystRoom
              </span>
            </Link>
            
            <p className="text-primary-foreground/70 text-base leading-relaxed max-w-sm mb-6">
              {footer?.footer_text || 'Futbolu positional play perspektifinden okumak. Taktik analiz, oyun modelleri ve modern futbolun derinliklerine yolculuk.'}
            </p>

            {/* Decorative Tactical Element */}
            <div className="flex items-center gap-3 opacity-40">
              <div className="w-12 h-6 border border-primary-foreground/50 rounded-sm" />
              <svg width="40" height="20" viewBox="0 0 40 20" className="text-primary-foreground">
                <path 
                  d="M0 10 L30 10 M25 5 L30 10 L25 15" 
                  stroke="currentColor" 
                  strokeWidth="1.5" 
                  fill="none"
                />
              </svg>
              <div className="w-4 h-4 rounded-full border-2 border-primary-foreground/50" />
            </div>
          </div>

          {/* Links Columns */}
          <div className="md:col-span-7">
            {footer ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-8">
                {/* İçerik */}
                {footer.primary_links && footer.primary_links.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold tracking-widest uppercase text-primary-foreground/50 mb-4">
                      İçerik
                    </h4>
                    <ul className="space-y-3">
                      {footer.primary_links.map((link, index) => (
                        <li key={index}>
                          <Link 
                            to={link.href}
                            className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                          >
                            {link.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Site */}
                {footer.secondary_links && footer.secondary_links.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold tracking-widest uppercase text-primary-foreground/50 mb-4">
                      Site
                    </h4>
                    <ul className="space-y-3">
                      {footer.secondary_links.map((link, index) => (
                        <li key={index}>
                          <Link 
                            to={link.href}
                            className="text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                          >
                            {link.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Sosyal */}
                {footer.social_links && footer.social_links.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold tracking-widest uppercase text-primary-foreground/50 mb-4">
                      Sosyal
                    </h4>
                    <ul className="space-y-3">
                      {footer.social_links.map((link, index) => (
                        <li key={index}>
                          <a 
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-primary-foreground/70 hover:text-primary-foreground transition-colors"
                          >
                            {link.label}
                            <ArrowUpRight className="h-3 w-3" />
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-8">
                <div className="animate-pulse">
                  <div className="h-4 bg-primary-foreground/20 rounded mb-4"></div>
                  <div className="space-y-3">
                    <div className="h-3 bg-primary-foreground/10 rounded"></div>
                    <div className="h-3 bg-primary-foreground/10 rounded"></div>
                  </div>
                </div>
                <div className="animate-pulse">
                  <div className="h-4 bg-primary-foreground/20 rounded mb-4"></div>
                  <div className="space-y-3">
                    <div className="h-3 bg-primary-foreground/10 rounded"></div>
                    <div className="h-3 bg-primary-foreground/10 rounded"></div>
                    <div className="h-3 bg-primary-foreground/10 rounded"></div>
                  </div>
                </div>
                <div className="animate-pulse">
                  <div className="h-4 bg-primary-foreground/20 rounded mb-4"></div>
                  <div className="space-y-3">
                    <div className="h-3 bg-primary-foreground/10 rounded"></div>
                    <div className="h-3 bg-primary-foreground/10 rounded"></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-primary-foreground/10">
        <div className="container-wide py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-primary-foreground/50">
              © {currentYear} TheAnalystRoom — Süleyman Kapıcıoğlu
            </p>
            <div className="flex items-center gap-6">
              <Link 
                to="/rss.xml" 
                className="text-xs text-primary-foreground/50 hover:text-primary-foreground/70 transition-colors"
              >
                RSS
              </Link>
              <Link 
                to="/sitemap.xml" 
                className="text-xs text-primary-foreground/50 hover:text-primary-foreground/70 transition-colors"
              >
                Sitemap
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
